import React, { useEffect, useState } from "react";
import Select from "web/react/components/select/select";
import { Option } from "web/react/components/select/types";
import { SizeOption } from "web/react/components/size-picker/size-option";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { Features } from "web/types/analytics";
import { Schemas } from "web/types/examples/schemas";
import { BuyBuyBuyAreaSize as Size } from "web/types/serializers";
import {
    SelectableSchemaSizePickerOptionHeading,
    SizePickerOptionHeading,
    SizePickerOptionHeadingProps,
} from "./option-heading";
import styles from "./size-picker.module.css";

export function compileSizeOptions(sizes: Size[]): Option[] {
    return sizes.map((size) => {
        const inStock = size.in_stock === undefined ? true : size.in_stock;
        return {
            selectedText: size.human_size,
            text: [size.human_size],
            value: size,
            disabled: !inStock,
        };
    });
}

function sizePickerFooter(): JSX.Element {
    const sizePickerDisclaimerLabel = gettext("product.buy_area.size_picker.supply_label");
    return (
        <div>
            <div className={styles.divider}></div>
            <div className={styles.disclaimer}>{sizePickerDisclaimerLabel}</div>
        </div>
    );
}

export function compileLocalizedSizeOptions(sizes: Size[], schema: string): Option[] {
    // Sometimes there are international conversions for some sizes and not others
    // This will allow the most schemas to be shown and will filter out the sizes not
    // converted into that schema.
    // These size pickers containing localised sizes are branded: New Taxonomy
    const validSizes = sizes.filter((size: Size) => {
        if (size.localized_sizes?.[schema]) {
            return size;
        }
    });
    return validSizes.map((size: Size) => {
        const localizedSizeText = size.localized_sizes?.[schema].display_size as string;
        const retailerSizeText = size.retailer_size?.display_size;
        const inStock = size.in_stock === undefined ? true : size.in_stock;
        const selectedText =
            localizedSizeText === retailerSizeText
                ? localizedSizeText
                : `${localizedSizeText} / ${retailerSizeText}`;

        return {
            selectedText,
            text: [localizedSizeText, retailerSizeText],
            value: size,
            disabled: !inStock,
        } as Option;
    });
}

function createSelectableSchemaSizePickerOptionHeading(
    onSchemaSelect,
    schemas,
    selectedSchema
): (SizePickerOptionHeadingProps: SizePickerOptionHeadingProps) => JSX.Element {
    return function SelectableSchemaSizePickerOptionHeadingWrapper({
        onCloseButtonClick,
    }: SizePickerOptionHeadingProps): JSX.Element {
        return (
            <SelectableSchemaSizePickerOptionHeading
                onCloseButtonClick={onCloseButtonClick}
                onSchemaSelect={onSchemaSelect}
                schemas={schemas}
                selectedSchema={selectedSchema}
            />
        );
    };
}

export interface SizePickerProps {
    isExpanded: boolean;
    onSelect: (option: Option) => void;
    schemas: Schemas;
    setExpanded: (expanded: boolean) => void;
    sizes: Size[]; // supporting n-columns, envisioned n=[1,3]
    userDefaultSchema: string;
    defaultSelectText?: string;
    error?: boolean;
    productType: "affiliate" | "checkout";
    showDisabledOptions?: boolean;
}

export default function SizePicker({
    isExpanded,
    onSelect,
    schemas,
    setExpanded,
    sizes,
    userDefaultSchema,
    defaultSelectText = "Select a size",
    error = false,
    productType,
    showDisabledOptions = false,
}: SizePickerProps): JSX.Element {
    // TODO: i18n
    const [selectedSchema, setSelectedSchema] = useState(userDefaultSchema);

    const localizedSizes = sizes.filter((size: Size) => {
        return size.localized_sizes;
    });

    let hasLocalizedSizes = !!selectedSchema && localizedSizes.length > 0;

    const category = "size_picker";

    function getSizeOptions(): Option[] {
        if (hasLocalizedSizes) {
            const localizedSizeOptions = compileLocalizedSizeOptions(
                localizedSizes as Size[],
                selectedSchema
            );

            // Make sure at least one option is available
            const areOptionsAvailable = localizedSizeOptions.some((option: Option) => {
                return !option.disabled;
            });
            if (areOptionsAvailable) return localizedSizeOptions;
        }

        hasLocalizedSizes = false;
        return compileSizeOptions(sizes);
    }

    const [sizeOptions, setSizeOptions] = useState<Option[]>(getSizeOptions());

    useEffect(() => {
        analytics.event(category, "shown");
    }, []);

    useEffect(() => {
        setSizeOptions(getSizeOptions());
    }, [selectedSchema]);

    function getFeatures(): Features {
        const features = {
            selectable_schema: hasLocalizedSizes,
        };
        return features;
    }

    function handleFirstExpand(): void {
        const action = "first_expansion";
        const subtype = `checkout.${category}.${action}`;

        // Trigger an analytic to send:
        // - Num Sizes
        // - Which schema is initially selected
        // - What features the size picker contains
        analytics.event(category, action, "", false, null, subtype, {
            size_picker: {
                first_expansion: {
                    current_schema: selectedSchema,
                    features: getFeatures(),
                    sizes_available: localizedSizes.length || sizes.length,
                },
            },
        });
    }

    function handleSchemaSelect(event): void {
        const toSchema = event.target.value;
        const fromSchema = selectedSchema;

        setSelectedSchema(toSchema);

        const category = "size_picker";
        const action = "schema_change";
        const subType = `${productType}.${category}.${action}`;

        // Track which schema the user was on, and what they switched to
        analytics.event(category, action, "", false, null, subType, {
            size_picker: {
                schema_change: {
                    from_schema: fromSchema,
                    to_schema: toSchema,
                },
            },
        });
    }

    function onTouchOverlay(): void {
        setExpanded(false);
    }

    return (
        <>
            {isExpanded && <div className={styles.overlay} onTouchStart={onTouchOverlay}></div>}
            <Select
                dataTestId="size-picker-select"
                defaultSelectText={defaultSelectText}
                error={error}
                isExpanded={isExpanded}
                onFirstExpand={handleFirstExpand}
                onSelect={onSelect}
                onDisabledSelect={onSelect}
                OptionComponent={SizeOption}
                OptionHeadingComponent={
                    hasLocalizedSizes
                        ? createSelectableSchemaSizePickerOptionHeading(
                              handleSchemaSelect,
                              schemas,
                              selectedSchema
                          )
                        : SizePickerOptionHeading
                }
                OptionFooterComponent={sizePickerFooter()}
                options={sizeOptions}
                setExpanded={setExpanded}
                showOnlyOptionByDefault={true}
                showDisabledOptions={showDisabledOptions}
            />
        </>
    );
}
